import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { formatNumber } from "../utils/format.ts";

interface PanelProps {
  title?: ReactNode;
  /** One line under the title: what the section shows. */
  subtitle?: ReactNode;
  /** A grey number after the title (rows, items). */
  count?: number;
  /** Right of the title: a small button, a select, a menu. */
  action?: ReactNode;
  /**
   * Content runs edge to edge under a divided header (a table, a list of
   * rows); without it the content sits in the panel's padding.
   */
  disablePadding?: boolean;
  /** Fill the grid cell, for panels side by side. */
  fill?: boolean;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/** A titled section: the one card every page section is drawn in. */
export default function Panel({
  title,
  subtitle,
  count,
  action,
  disablePadding = false,
  fill = false,
  sx,
  children,
}: PanelProps) {
  const hasHeader = title != null || subtitle != null || action != null;
  const header = hasHeader && (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        rowGap: 1,
        ...(disablePadding
          ? { px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }
          : { mb: 1.5 }),
      }}
    >
      {/* Grows from a small basis, so a long subtitle wraps instead of pushing the action down. */}
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        {title != null && (
          <Typography variant="h5" component="h2">
            {title}
            {count != null && (
              <Typography component="span" variant="inherit" sx={{ color: "text.secondary", fontWeight: 500, ml: 1 }}>
                {formatNumber(count)}
              </Typography>
            )}
          </Typography>
        )}
        {subtitle != null && (
          <Typography variant="body2" component="div" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action != null && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{action}</Box>
      )}
    </Stack>
  );

  return (
    <Card sx={[{ ...(fill && { height: "100%" }) }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {disablePadding ? (
        <>
          {header}
          {children}
        </>
      ) : (
        <Box sx={{ p: 2 }}>
          {header}
          {children}
        </Box>
      )}
    </Card>
  );
}
